import axios from 'axios';
/**
 * Standalone Medable two-step file-upload primitive built on the modern
 * `apiClient` (axios-style). It mirrors the legacy classic sequence
 * (`siteapp/.../WetSignUpload` + `framework/.../FileManager/upload.js`) without
 * copying it: request an upload token from Cortex, push the bytes to the
 * returned storage URL, then poll the media facet until it is ready.
 *
 * It is intentionally decoupled from any specific component so it can be reused
 * for any Medable File property, not just the PTS `ec__final_document`.
 */
/** 50 MB, matching the legacy `wsFileUploadLimit.maxSize`. */
export const MEDABLE_UPLOAD_MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;
export const MEDABLE_UPLOAD_ACCEPTED_MIME_TYPE = 'application/pdf';
const DEFAULT_PROPERTY_PATH = 'ec__final_document';
const DEFAULT_POLL_INTERVAL_MS = 1000;
// Retries AFTER the initial read, so the facet is read up to 1 + 20 = 21 times
// over ~20s. Mirrors the legacy classic poll cadence of ~20 × 1s.
const DEFAULT_MAX_POLL_RETRIES = 20;
export class MedableFileUploadError extends Error {
    constructor(code, message) {
        super(message);
        this.name = 'MedableFileUploadError';
        this.code = code;
    }
}
const isPdfFile = (file) => file.type === MEDABLE_UPLOAD_ACCEPTED_MIME_TYPE || file.name.toLowerCase().endsWith('.pdf');
/** Validates a file against the PDF mime + size limits before any network call. */
export const validateMedableUploadFile = (file) => {
    if (!isPdfFile(file)) {
        throw new MedableFileUploadError('invalid_type', 'Only PDF files can be uploaded.');
    }
    if (file.size > MEDABLE_UPLOAD_MAX_FILE_SIZE_BYTES) {
        throw new MedableFileUploadError('file_too_large', 'The file exceeds the 50 MB upload limit.');
    }
};
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
/** Unwraps the `{ object: 'result', data }` envelope Cortex returns for facet writes. */
const unwrapUploadResponse = (body) => (body?.data ?? body ?? {});
/**
 * The created-facet `path` comes back without the `/v2` API-version segment that
 * the apiClient `baseURL` omits, so prefix it for the poll read. Classic polled
 * `/v2/accounts/{id}/ec__site_documents/{id}/{prop}/content`.
 */
const toPollPath = (path) => path.startsWith('/v2/') ? path : `/v2${path.startsWith('/') ? '' : '/'}${path}`;
/**
 * Cortex reports an in-flight upload with a `202 kMediaNotReady` fault body and,
 * once processed, returns the populated facet. Axios resolves the 202, so treat
 * an empty body or a not-ready fault as "keep polling"; any other non-empty
 * object is a populated facet and counts as ready.
 */
const isMediaReady = (value) => {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const media = value;
    if (media.object === 'fault' || media.code === 'kMediaNotReady') {
        return false;
    }
    return Object.keys(media).length > 0;
};
const defaultPerformUpload = async (token, file) => {
    const formData = new FormData();
    for (const { key, value } of token.fields) {
        formData.set(key, value);
    }
    formData.set('file', file);
    // Cross-origin push to object storage: never send Medable credentials here.
    // Do NOT set Content-Type manually — axios derives `multipart/form-data; boundary=...`
    // from the FormData body, and a hand-set header would drop the boundary and break S3.
    await axios.post(token.uploadUrl, formData, {
        withCredentials: false,
    });
};
/**
 * Polls the created facet path until Cortex reports the media as processed
 * (a non-empty body): one immediate read followed by up to `maxPollRetries`
 * retries spaced `pollIntervalMs` apart.
 */
const pollUntilMediaReady = async (apiClient, path, pollIntervalMs, maxPollRetries) => {
    let media = (await apiClient.get(path)).data;
    let retriesRemaining = maxPollRetries;
    while (!isMediaReady(media) && retriesRemaining > 0) {
        await delay(pollIntervalMs);
        media = (await apiClient.get(path)).data;
        retriesRemaining -= 1;
    }
    if (!isMediaReady(media)) {
        throw new MedableFileUploadError('poll_timeout', 'Timed out waiting for the uploaded file to be processed.');
    }
    return media;
};
/**
 * Uploads `file` to the `propertyPath` File property of a signed document and
 * resolves once the media is ready.
 *
 * Sequence (mirrors legacy):
 *   1. `POST /v2/account/{accountId}/ec__site_documents/{signedDocId}/{propertyPath}` with `{ content: file.name }`
 *      — the response is wrapped in a `{ object: 'result', data }` envelope.
 *   2. Push bytes to `uploads[0]`
 *   3. Poll the `/v2`-prefixed facet `path` until media-ready (`state: 2`)
 */
export const uploadMedableFile = async ({ apiClient, accountId, signedDocId, file, propertyPath = DEFAULT_PROPERTY_PATH, pollIntervalMs = DEFAULT_POLL_INTERVAL_MS, maxPollRetries = DEFAULT_MAX_POLL_RETRIES, performUpload = defaultPerformUpload, }) => {
    validateMedableUploadFile(file);
    const facetUrl = `/v2/account/${accountId}/ec__site_documents/${signedDocId}/${propertyPath}`;
    const { data } = await apiClient.post(facetUrl, { content: file.name });
    const payload = unwrapUploadResponse(data);
    const uploadToken = payload.uploads?.[0];
    if (!uploadToken) {
        throw new MedableFileUploadError('missing_upload_token', 'No upload token was returned for the file property.');
    }
    await performUpload(uploadToken, file);
    if (payload.path) {
        await pollUntilMediaReady(apiClient, toPollPath(payload.path), pollIntervalMs, maxPollRetries);
    }
    return payload;
};
